import { plural } from '@/lib/format'
import type { ScheduleEvent, WeddingData } from '@/mock/types'
import { taskPromptState } from '@/patterns/prompts'
import { clock, minutes, scheduleGroups } from './model'

/** `event` opens the event sheet prefilled; the couple picks the time. */
export type SchedulePromptAction =
  | { kind: 'event'; event: Partial<ScheduleEvent> & { title: string } }
  | { kind: 'task'; title: string }

export type SchedulePromptRule = {
  code: string
  title: string
  hint: string
  action: SchedulePromptAction
}

const outdoors = /zahrad|park|nádvoří|vyhlídk|teras|louk|pláž|venku/i
const rainPlan = /déšť|dešt|deštiv|pršet/i
const mealSuppliers = new Set(['catering', 'venue', 'cake-sweets', 'bar'])
// A supplier on site this long needs a meal and a break.
const longStay = 5 * 60

/**
 * "Na co nezapomenout" for Harmonogram: what the day is missing, from the
 * schedule and the rest of the plan (guests who need transport, the
 * photographer, suppliers on site all day). An event prompt disappears once
 * the event is there; a task prompt once its task is done.
 */
export function schedulePrompts(
  data: WeddingData,
): (SchedulePromptRule & { state: 'open' | 'in-tasks' })[] {
  const active = data.schedule.filter((event) => !event.removed)
  const suppliers = data.suppliers.filter((supplier) => supplier.status !== 'cancelled')
  const supplierOf = (event: ScheduleEvent) =>
    event.owner?.type === 'supplier'
      ? suppliers.find((supplier) => supplier.id === event.owner?.id)
      : undefined
  const has = (pattern: RegExp) => active.some((event) => pattern.test(event.title))

  const ceremony = active.find((event) => event.source?.section === 'ceremony')
  const guestsArrive =
    ceremony?.time &&
    active.some(
      (event) =>
        event.audience === 'guests' &&
        event.id !== ceremony.id &&
        event.time &&
        minutes(event.time) < minutes(ceremony.time as string),
    )
  const preparations = scheduleGroups(data)[0]?.events.length ?? 0
  const photographer = suppliers.find((supplier) => supplier.category === 'photographer')
  const transportParties = data.parties.filter(
    (party) => party.transport && party.persons.some((person) => person.attendance !== 'declined'),
  ).length
  const transportPlanned =
    has(/odvoz|rozvoz|autobus|kyvadl/i) ||
    active.some((event) => supplierOf(event)?.category === 'transport')

  const stays = new Map<string, { name: string; start: number; end: number }>()
  for (const event of active) {
    const supplier = supplierOf(event)
    if (!supplier || !event.time || mealSuppliers.has(supplier.category)) continue
    const start = minutes(event.time)
    const end = event.endTime ? Math.max(start, minutes(event.endTime)) : start
    const stay = stays.get(supplier.id)
    stays.set(supplier.id, {
      name: supplier.name,
      start: Math.min(stay?.start ?? start, start),
      end: Math.max(stay?.end ?? end, end),
    })
  }
  const allDay = [...stays.values()].filter((stay) => stay.end - stay.start >= longStay)

  const outside = active.filter(
    (event) => outdoors.test(event.location) && !rainPlan.test(event.description),
  )
  const coordinator = suppliers.some(
    (supplier) => supplier.category === 'coordination' || supplier.category === 'moderator',
  )
  const lastEnd = Math.max(
    0,
    ...active.map((event) => (event.endTime ? minutes(event.endTime) : 0)),
  )

  const rules: (SchedulePromptRule | false | undefined)[] = [
    !!ceremony?.time &&
      !guestsArrive && {
        code: 'schedule-guest-arrival',
        title: 'Kdy mají hosté dorazit?',
        hint: `Obřad začíná ve ${ceremony.time}. Na příjezd a usazení potřebují hosté aspoň půl hodiny.`,
        action: {
          kind: 'event',
          event: {
            title: 'Příjezd hostů',
            audience: 'guests',
            endTime: ceremony.time,
            dayPartId: ceremony.dayPartId,
            location: ceremony.location,
          },
        },
      },
    active.length > 0 &&
      preparations === 0 && {
        code: 'schedule-getting-ready',
        title: 'Kdy se začnete chystat?',
        hint: 'Líčení, účes a oblékání zaberou celé dopoledne. Ráno je dobré mít rozvržené.',
        action: { kind: 'event', event: { title: 'Přípravy nevěsty', dayPartId: null } },
      },
    photographer &&
      !has(/skupinov|společn\S* fot/i) && {
        code: 'schedule-group-photo',
        title: 'Kdy bude společná fotka?',
        hint: 'Po obřadu se rodina a přátelé rychle rozejdou. Domluvte s fotografem čas hned po něm.',
        action: {
          kind: 'event',
          event: {
            title: 'Skupinové focení',
            audience: 'guests',
            dayPartId: ceremony?.dayPartId ?? null,
            owner: { type: 'supplier', id: photographer.id },
          },
        },
      },
    transportParties > 0 &&
      !transportPlanned && {
        code: 'schedule-transport',
        title: 'Kdy pojede odvoz hostů?',
        hint: `${transportParties} ${plural(transportParties, 'pozvánka potřebuje', 'pozvánky potřebují', 'pozvánek potřebuje')} odvoz, v harmonogramu zatím není.`,
        action: {
          kind: 'event',
          event: {
            title: 'Odvoz hostů',
            audience: 'guests',
            dayPartId: data.dayParts.at(-1)?.id ?? null,
          },
        },
      },
    allDay.length > 0 &&
      !has(/pro dodavatele|pro tým/i) && {
        code: 'schedule-supplier-meals',
        title: 'Kdy se najedí dodavatelé?',
        hint: `${list(allDay.map((stay) => stay.name))} ${allDay.length > 1 ? 'jsou' : 'je'} na místě přes 5 hodin. Catering s nimi musí počítat.`,
        action: { kind: 'event', event: { title: 'Jídlo pro dodavatele', audience: 'team' } },
      },
    outside.length > 0 && {
      code: 'schedule-rain',
      title: 'Co když bude pršet?',
      hint: `${outside.length} ${plural(outside.length, 'událost je', 'události jsou', 'událostí je')} venku bez plánu B: ${outside
        .slice(0, 2)
        .map((event) => event.title)
        .join(', ')}${outside.length > 2 ? '…' : '.'}`,
      action: { kind: 'task', title: 'Domluvit plán B pro případ deště' },
    },
    active.length > 0 &&
      !coordinator && {
        code: 'schedule-coordinator',
        title: 'Kdo bude v den svatby řídit program?',
        hint: 'Ať se dodavatelé neptají vás. Stačí svědek nebo kamarád, který zná harmonogram.',
        action: { kind: 'task', title: 'Domluvit koordinátora svatebního dne' },
      },
    lastEnd > 22 * 60 && {
      code: 'schedule-music-end',
      title: 'Do kolika může hrát hudba?',
      hint: `Zábava končí v ${clock(lastEnd)}. Ověřte s místem noční klid a hlasitost.`,
      action: { kind: 'task', title: 'Ověřit s místem, do kolika může hrát hudba' },
    },
  ]

  const hidden = new Set(data.hiddenPrompts)
  return rules.flatMap((rule) => {
    if (!rule || hidden.has(rule.code)) return []
    if (rule.action.kind !== 'task') return [{ ...rule, state: 'open' as const }]
    const state = taskPromptState(data.tasks, rule.action.title)
    return state === 'done' ? [] : [{ ...rule, state }]
  })
}

/** "A, B a C". */
function list(items: string[]) {
  return items.length > 1 ? `${items.slice(0, -1).join(', ')} a ${items.at(-1)}` : (items[0] ?? '')
}
